import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import type { Theme } from "src/theme";

import {
  Button,
  TextField,
  Grid,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";

import { useSelector } from "react-redux";

import AddEditAnalyticsGroupingsDialog from "src/pages/RootDialogs/ProfileSettings/AnalyticsGroupings/AddEditAnalyticsGroupingsDialog";
import { AnalyticsGroupingItem } from "src/interfaces/systemState.interface";
import { getSaleByOriginalId } from "src/store/sale/selector";
import BrandField from "src/components/BrandField";
import VendorField from "src/components/VendorField";
import SalePlatformField from "src/components/SalePlatformField";
import cleanListWithExtraValue from "src/utils/cleanListWithExtraValue";
import NumberField from "src/components/NumberField";
import { userGetInventoryTypeSelector } from "src/store/system/selector";
import InfoTooltip from "src/components/InfoTooltip";
import GoldIconButton from "src/components/GoldIconButton";

import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";
import Autocomplete from "src/components/Autocomplete";
import Tooltip from "src/components/Tooltip";
import firestore from "src/apiService/firestore";
import { consolidateOrders } from "src/apiService/modules/ebay";
import ConditionalTooltip from "src/components/ConditionalTooltip";

import WalkThrough from "./EditSaleBasicWalkThrough";

const useStyles = makeStyles((theme: Theme) => ({
  padLeft: {
    marginLeft: 34,
  },
  flexGrid: {
    display: "flex",
    justifyContent: "space-between",
  },
  pointer: {
    "& *": {
      cursor: "pointer !important",
    },
    cursor: "pointer !important",
  },
  deleteButton: {
    position: "absolute",
    right: theme.spacing(3),
    top: theme.spacing(1.5),
    color: theme.palette.error.main,
  },
}));

const EditSaleBasicDialog = ({
  handleDelete,
  loading,
  errors,
  values,
  setValues,
  handleChange,
  itemOptionsLoading,
  departments: initialDepartments,
  renderOption,
  categories: initialCategories,
  subCategories: initialSubCategories,
  allPlatforms: platforms,
  handleMove,
  goToBundle,
  goToReturn,
  handleSave,
  editReturn,
}) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  const isInventoryDisabled = useSelector(
    (s: any) => userGetInventoryTypeSelector(s) === "cash"
  );
  const [openAddAnalyticsGrouping, setOpenAddAnalyticsGrouping] =
    useState<AnalyticsGroupingItem>();
  const returnSale = useSelector((s: any) => getSaleByOriginalId(s, values.id));
  const hasReturn = !!returnSale || !!values.return_id || !!values.original_id;
  const hasReviewedReturn = hasReturn && !returnSale?.unreviewed;
  const salePlatform = values.sale_platform;
  const allPlatforms = useMemo(
    () => cleanListWithExtraValue(platforms, salePlatform),
    [platforms, salePlatform]
  );
  const department = values.department;
  const departments = useMemo(
    () => cleanListWithExtraValue(initialDepartments, department),
    [initialDepartments, department]
  );
  const category = values.category;
  const categories = useMemo(
    () => cleanListWithExtraValue(initialCategories, category),
    [initialCategories, category]
  );
  const subCategory = values.sub_category;
  const subCategories = useMemo(
    () => cleanListWithExtraValue(initialSubCategories, subCategory),
    [initialSubCategories, subCategory]
  );
  const [fixingShipping, setFixingShipping] = useState(false);

  return (
    <>
      <WalkThrough open={open} onClose={() => setOpen(false)} />
      <AddEditAnalyticsGroupingsDialog
        open={!!openAddAnalyticsGrouping}
        initialValue={openAddAnalyticsGrouping}
        onClose={(ag) => {
          setOpenAddAnalyticsGrouping(undefined);
          if (ag)
            setValues((v) => ({
              ...v,
              department: ag.department,
              category: ag.category,
              sub_category: ag.subcategory,
            }));
        }}
      />
      <DialogTitle>
        Edit Sale{hasReturn ? " (Refund Attached)" : ""}
        {!values.bundle_id && !hasReturn && (
          <IconButton
            onClick={handleDelete}
            className={classes.deleteButton}
            disabled={loading}
          >
            <DeleteOutlineOutlinedIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <DialogContentText className="relative" tabIndex={-1} component="div">
          <form noValidate autoComplete="off">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <div className={classes.padLeft}>
                  <TextField
                    error={errors.item_title ? true : false}
                    helperText={errors.item_title}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    label="Item Title"
                    value={values.item_title}
                    disabled={loading}
                    onChange={handleChange("item_title")}
                    required
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="Source where you purchased the item."
                  size={18}
                />
                <VendorField
                  margin="dense"
                  disabled={loading}
                  value={values.vendor}
                  onChange={(vendor) => {
                    setValues((values) => ({
                      ...values,
                      vendor: vendor || "",
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.padLeft}
                  disabled={itemOptionsLoading}
                  options={["Add new department", ...departments]}
                  value={departments.length ? values.department || null : null}
                  autoHighlight
                  onChange={(_e, value) => {
                    if (value === "Add new department") {
                      setOpenAddAnalyticsGrouping({
                        department: "",
                        category: "",
                        subcategory: "",
                      });
                      setValues({
                        ...values,
                        department: "",
                        category: "",
                        sub_category: "",
                      });
                      return;
                    }
                    setValues({
                      ...values,
                      department: value || "",
                      category: "",
                      sub_category: "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors.department ? true : false}
                      helperText={errors.department}
                      margin="dense"
                      required
                      label="Department"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={renderOption}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.padLeft}
                  disabled={itemOptionsLoading}
                  options={["Add new category", ...categories]}
                  value={categories.length ? values.category || null : null}
                  autoHighlight
                  onChange={(_e, value) => {
                    if (value === "Add new category") {
                      setOpenAddAnalyticsGrouping({
                        department: values.department,
                        category: "",
                        subcategory: "",
                      });
                      setValues({
                        ...values,
                        category: "",
                        sub_category: "",
                      });
                      return;
                    }
                    setValues({
                      ...values,
                      category: value || "",
                      sub_category: "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors.category ? true : false}
                      helperText={errors.category}
                      margin="dense"
                      required
                      label="Category"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={renderOption}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  className={classes.padLeft}
                  disabled={itemOptionsLoading}
                  options={["Add new sub-category", ...subCategories]}
                  value={
                    subCategories.length ? values.sub_category || null : null
                  }
                  autoHighlight
                  onChange={(_e, value) => {
                    if (value === "Add new sub-category") {
                      setOpenAddAnalyticsGrouping({
                        department: values.department,
                        category: values.category,
                        subcategory: "",
                      });
                      setValues({ ...values, sub_category: "" });
                      return;
                    }
                    setValues({ ...values, sub_category: value || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors.sub_category ? true : false}
                      helperText={errors.sub_category}
                      margin="dense"
                      required
                      label="Sub category"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={renderOption}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BrandField
                  className={classes.padLeft}
                  value={values.brand}
                  onChange={(e, value) =>
                    handleChange("brand")({ target: { value } })
                  }
                  disabled={loading}
                  TextFieldProps={{
                    error: errors.brand ? true : false,
                    helperText: errors.brand,
                    fullWidth: true,
                    variant: "outlined",
                    margin: "dense",
                    label: "Brand",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip text="Where you have stored the item." size={18} />
                <TextField
                  error={errors.location ? true : false}
                  helperText={errors.location}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Location"
                  value={values.location}
                  disabled={loading}
                  onChange={handleChange("location")}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <div className={classes.padLeft}>
                  <TextField
                    error={errors.sku ? true : false}
                    helperText={errors.sku}
                    className="w-full"
                    variant="outlined"
                    margin="dense"
                    label="SKU"
                    value={values.sku}
                    disabled={loading}
                    onChange={handleChange("sku")}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The total revenue collected from a customer. This includes the price of the item and additional money collected for shipping. This does not include sales tax."
                  size={18}
                />
                <NumberField
                  required
                  error={errors.sale_price ? true : false}
                  helperText={errors.sale_price}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Sale Price"
                  value={
                    values.bundle_id ? values.sale_price : values.sale_price
                  }
                  disabled={loading || !!values.bundle_id}
                  onChange={handleChange("sale_price", true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fixed={2}
                />
              </Grid>
              {isInventoryDisabled ? null : (
                <Grid item xs={12} sm={4} className={classes.flexGrid}>
                  <InfoTooltip
                    text="The price at which you purchased the item."
                    size={18}
                  />
                  <NumberField
                    error={errors.purchase_price ? true : false}
                    helperText={errors.purchase_price}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    label="Purchase Price"
                    value={values.purchase_price}
                    disabled={loading}
                    onChange={handleChange("purchase_price", true)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    fixed={2}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The amount you paid to ship this sale. If a separate shipping transaction has been linked to the sale, click the link here to view it."
                  size={18}
                />
                {values.shipping_cost_analytics ? (
                  <Tooltip
                    title={
                      values.shipping_cost
                        ? "You have multiple shipping expenses assigned to this sale. Click here to correct this."
                        : "Click to view attached expenses."
                    }
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Shipping Cost"
                      disabled={fixingShipping}
                      value={
                        fixingShipping
                          ? "Fixing..."
                          : (
                              (values.shipping_cost || 0) +
                              (values.shipping_cost_analytics || 0)
                            ).toFixed(2)
                      }
                      InputProps={{
                        readOnly: true,
                        ...(fixingShipping
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CircularProgress size={18} />
                                </InputAdornment>
                              ),
                            }
                          : {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            }),
                      }}
                      className={classes.pointer}
                      onClick={async () => {
                        if (fixingShipping) return;

                        if (values.shipping_cost) {
                          if (values.transaction_id && values.id) {
                            setFixingShipping(true);
                            await firestore()
                              .collection("Sales")
                              .doc(values.id)
                              .update({
                                shipping_cost: 0,
                              });
                            await consolidateOrders({
                              orderIds: [values.transaction_id],
                            });
                            setFixingShipping(false);
                          }

                          return;
                        }
                        window.open(
                          `/transaction-details/expenses?order-id=${encodeURIComponent(
                            values.transaction_id
                          )}`
                        );
                      }}
                    />
                  </Tooltip>
                ) : (
                  <NumberField
                    error={errors.shipping_cost ? true : false}
                    helperText={errors.shipping_cost}
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    label="Shipping Cost"
                    value={values.shipping_cost}
                    disabled={loading || !!values.bundle_id}
                    onChange={handleChange("shipping_cost", true)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    fixed={2}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The amount the platform charged you to sell the item."
                  size={18}
                />
                <NumberField
                  error={errors.transaction_fees ? true : false}
                  helperText={errors.transaction_fees}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Transaction Fees"
                  value={
                    values.bundle_id
                      ? values.transaction_fees
                      : values.transaction_fees
                  }
                  disabled={loading || !!values.bundle_id}
                  onChange={handleChange("transaction_fees", true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fixed={2}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="If other expenses have been linked to this sale, click the link here to view them."
                  size={18}
                />
                <Tooltip title="Click to view attached expenses.">
                  <TextField
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    label="Other Fees"
                    value={(values.other_fees || 0).toFixed(2)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      readOnly: true,
                    }}
                    InputLabelProps={{
                      className: "whitespace-nowrap",
                    }}
                    className={classes.pointer}
                    onClick={() => {
                      window.open(
                        `/transaction-details/expenses?order-id=${encodeURIComponent(
                          values.transaction_id
                        )}`
                      );
                    }}
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The sales tax paid by the buyer."
                  size={18}
                />
                <NumberField
                  error={errors.sales_tax ? true : false}
                  helperText={errors.sales_tax}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Sales Tax"
                  value={values.bundle_id ? values.sales_tax : values.sales_tax}
                  disabled={loading || !!values.bundle_id}
                  onChange={handleChange("sales_tax", true)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fixed={2}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <div className={classes.padLeft}>
                  <TextField
                    onInput={(e: any) => {
                      let value = e.target?.value;
                      let res = value ? value : "";
                      setValues({ ...values, sale_state: res });
                      e.target.value = res;
                    }}
                    value={values.sale_state}
                    margin="dense"
                    label="Sale State"
                    variant="outlined"
                    fullWidth
                    disabled={loading || !!values.bundle_id}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="If you aren’t responsible for filing sales tax, mark this field as ‘No’. If you are responsible for filing sales tax, mark this field as ‘Yes’."
                  size={18}
                />
                <Autocomplete
                  fullWidth
                  disabled={itemOptionsLoading || !!values.bundle_id}
                  options={["No", "Yes"]}
                  value={values.liable_to_pay ? "Yes" : "No"}
                  autoHighlight
                  onChange={(_e, value) => {
                    setValues({
                      ...values,
                      liable_to_pay: value === "Yes" ? true : false,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors.liable_to_pay ? true : false}
                      helperText={errors.liable_to_pay}
                      margin="dense"
                      required
                      label="Liable to Pay"
                      fullWidth
                      variant="outlined"
                    />
                  )}
                  renderOption={renderOption}
                />
              </Grid>
              {isInventoryDisabled ? null : (
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    className={classes.padLeft}
                    multiple
                    options={allPlatforms}
                    value={
                      values.platforms_listed ? values.platforms_listed : []
                    }
                    onChange={handleChange("platforms_listed")}
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        label="Platforms Listed"
                        variant="outlined"
                      />
                    )}
                    renderOption={renderOption}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text={`Record the platform where you sold this item. If the platform doesn't appear in the dropdown, you can add platforms in your profile settings or by clicking "Add New Platform" in the "Sale Platform" dropdown."`}
                  size={18}
                />
                <SalePlatformField
                  disabled={itemOptionsLoading || !!values.bundle_id}
                  value={{
                    name: values.sale_platform,
                    id: values.sale_platform_account_id,
                  }}
                  onChange={(value) => {
                    setValues((values) => ({
                      ...values,
                      sale_platform: value.name || "",
                      sale_platform_account_id: value.id,
                    }));
                  }}
                  margin="dense"
                  required
                  error={!!errors.sale_platform}
                  helperText={errors.sale_platform}
                />
              </Grid>
              <Grid item xs={12} sm={8} className={classes.flexGrid}>
                <InfoTooltip text="Add notes to your sale." size={18} />
                <TextField
                  error={errors.notes ? true : false}
                  helperText={errors.notes}
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label="Notes"
                  value={values.notes || ""}
                  disabled={loading}
                  onChange={handleChange("notes")}
                />
              </Grid>
              {isInventoryDisabled ? null : (
                <Grid item xs={12} sm={4} className={classes.flexGrid}>
                  <InfoTooltip
                    text="The date that you purchased this item."
                    size={18}
                  />
                  <ZonedKeyboardDatePicker
                    disableToolbar
                    error={!!errors.purchase_date}
                    helperText={errors.purchase_date}
                    fullWidth
                    variant="inline"
                    inputVariant="outlined"
                    format="MM/dd/yyyy"
                    autoOk={true}
                    margin="dense"
                    label="Purchase Date"
                    required
                    value={values.purchase_date || null}
                    disabled={loading}
                    onChange={(d) => {
                      if (!d) return;
                      setValues({ ...values, purchase_date: d });
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The date that you listed this item for sale."
                  size={18}
                />
                <ZonedKeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  fullWidth
                  inputVariant="outlined"
                  format="MM/dd/yyyy"
                  margin="dense"
                  label="List Date"
                  autoOk={true}
                  value={values.list_date || null}
                  disabled={loading}
                  onChange={(d) => {
                    if (d) {
                      setValues({ ...values, list_date: d });
                    } else {
                      const { list_date, ...v } = values;
                      setValues({
                        ...v,
                        list_date: null,
                      });
                    }
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} className={classes.flexGrid}>
                <InfoTooltip
                  text="The date that you sold this item."
                  size={18}
                />
                <ZonedKeyboardDatePicker
                  disableToolbar
                  fullWidth
                  variant="inline"
                  inputVariant="outlined"
                  format="MM/dd/yyyy"
                  margin="dense"
                  label="Sale Date"
                  autoOk={true}
                  required
                  error={!!errors.sale_date}
                  helperText={errors.sale_date}
                  value={values.sale_date || null}
                  disabled={loading || !!values.bundle_id}
                  onChange={(d) => {
                    if (!d) return;
                    setValues({ ...values, sale_date: d });
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
              </Grid>
              {isInventoryDisabled ? null : (
                <Grid item xs={12} sm={4} className={classes.flexGrid}>
                  <InfoTooltip
                    text="This is how you paid for the item that was sold. (ex. Cash on Hand, Chase Visa, PNC Debit, etc.). You can update this field by assigning this item to an inventory purchase."
                    size={18}
                  />
                  <ConditionalTooltip
                    condition={values.bank_account === "Balance Transfer"}
                    title="The inventory associated with this item was created through a return. Bank Account cannot be modified. To view the initial bank account associated with the purchase, search for the first sale of this item that was returned."
                  >
                    <TextField
                      value={values.bank_account || ""}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Bank Account"
                      disabled
                    />
                  </ConditionalTooltip>
                </Grid>
              )}
            </Grid>
          </form>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Box mt={-1.5}>
          <Tooltip title="Need help? Click here.">
            <GoldIconButton onClick={() => setOpen(true)} />
          </Tooltip>
        </Box>
        <Box flex={1} />
        {!values.bundle_id &&
          !hasReturn &&
          !values.original_id &&
          !isInventoryDisabled && (
            <Button
              onClick={handleMove}
              color="primary"
              variant="contained"
              disabled={loading || !!values.bundle_id || values.return_id}
              id="edit-sale-basic-move-to-inventory-button"
              tabIndex={-1}
            >
              Move to Inventory
            </Button>
          )}
        {!!values.bundle_id && !hasReturn && (
          <Button
            onClick={goToBundle}
            color="secondary"
            variant="contained"
            disabled={loading}
          >
            Edit Bundle
          </Button>
        )}
        {!hasReturn && !values.original_id && (
          <Button
            onClick={goToReturn}
            color="primary"
            variant="contained"
            disabled={loading}
            id="edit-sale-basic-record-refund-button"
            tabIndex={-1}
          >
            Record Refund
          </Button>
        )}
        {!!hasReturn && (
          <Button
            onClick={() => editReturn(values, !hasReviewedReturn)}
            color="primary"
            variant="contained"
          >
            {hasReviewedReturn ? "Edit Refund" : "Finalize Refund"}
          </Button>
        )}
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          Update
        </Button>
      </DialogActions>
    </>
  );
};

export default EditSaleBasicDialog;
